import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  createEnrollKey,
  downloadAgentShellQuery,
  downloadGpoPackage,
  GPO_FILENAME,
  saveEnrollKeyLimits,
  viewEnrollKey,
  type AgentVersions,
  type InstallCommands,
  type RawLimits,
} from '../../api/settingsDownloadAgent'
import { Forbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { AgentApiServerSection } from './AgentApiServerSection'
import './DownloadAgentPage.css'

/**
 * Settings -> Agent Install, element cu element fata de
 * `views/version-3/settings/download_agent/index.php`.
 *
 * TREI PASI. Cheia firmei, comanda de instalare, ce e dupa prima instalare.
 *
 * CE S-A PASTRAT DINADINS DIN ASEZAREA VECHE
 * ------------------------------------------
 * "View key" e VERDE si mai mare, iar "Refresh key" sta JOS, dupa o linie, cu
 * chenar rosu si cu explicatia langa el. Stateau umar la umar, doua butoane
 * mici la fel imbracate, iar cel din dreapta era cel care OPREA cheia: mana
 * mergea la el ori de cate ori voiai doar sa vezi cheia. Doua apasari care nu
 * au acelasi pret n-au ce cauta in acelasi loc si in aceeasi haina.
 *
 * CE E ALTFEL
 * -----------
 *  - Comenzile vin de la server, nu se lipesc aici. In portal se scriau in
 *    JavaScript, in macheta.
 *  - Un refuz vine ca 400 cu mesajul serverului, nu ca `status: ERROR` intr-un
 *    raspuns de 200.
 *  - Cardul instalatorului clasic 7.x apare numai cand adresa se stie: portalul
 *    lipea sirul si cand `UPDATE_SERVER` sau `OD_APP_KEY` lipseau, deci butonul
 *    ducea in gol.
 */

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/** Copiaza in clipboard, cu drumul de rezerva al machetei vechi. */
export function copyText(text: string, okMessage: string): void {
  if (text === '') return
  if (navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(text).then(() => toast(okMessage, 'Copied'))
    return
  }
  const area = document.createElement('textarea')
  area.value = text
  document.body.appendChild(area)
  area.select()
  try {
    document.execCommand('copy')
    toast(okMessage, 'Copied')
  } catch {
    /* fara clipboard: omul copiaza cu mana */
  }
  document.body.removeChild(area)
}

/** "Used 3 of 25 times · expires 2026-12-31", ca in macheta. */
export function usesText(limits: RawLimits): string {
  const uses = Number(limits.uses ?? 0)
  const parts = [`Used ${uses}`]
  if (limits.max_uses) parts.push(`of ${Number(limits.max_uses)}`)
  parts.push(uses === 1 ? 'time' : 'times')
  if (limits.expires_at) parts.push(`· expires ${String(limits.expires_at).substring(0, 10)}`)
  return parts.join(' ')
}

function Version({ value }: { value: string }) {
  return value !== '' ? <span className="od-da-ver">{value}</span> : null
}

function Step({ number, title, lead, children }: { number: number; title: string; lead: ReactNode; children: ReactNode }) {
  return (
    <div className="od-da-step">
      <div className="od-da-num">{number}</div>
      <div className="od-da-body">
        <h3>{title}</h3>
        <p className="od-da-lead">{lead}</p>
        {children}
      </div>
    </div>
  )
}

function CommandCard({ kind, icon, title, version, meta, command, copyLabel, note }: {
  kind: string
  icon: string
  title: string
  version: string
  meta: ReactNode
  command: string
  copyLabel: string
  note?: ReactNode
}) {
  return (
    <div className={`od-da-card ${kind}`}>
      <div className="od-da-os-icon">
        <i className={icon}></i>
      </div>
      <h4>
        {title}
        <Version value={version} />
      </h4>
      <p className="od-da-meta">{meta}</p>
      {note && <p className="od-da-note alert alert-warning">{note}</p>}
      <div className={command === '' ? 'od-da-term is-empty' : 'od-da-term'}>
        {command === '' ? 'The API address is not set on this installation, so the command cannot be built.' : command}
      </div>
      <button
        type="button"
        className="btn btn-primary btn-sm od-da-copy"
        disabled={command === ''}
        onClick={() => copyText(command, copyLabel)}
      >
        <i className="far fa-copy"></i> Copy command
      </button>
    </div>
  )
}

/** Pasul 1: cheia. Tot ce se poate apasa aici trece prin server si lasa urma. */
function KeyStep({ hasKey, canShowKey, canWrite, needsAdmin, limits, freshKey, onKey, onLimits }: {
  hasKey: boolean
  needsAdmin: boolean
  canShowKey: boolean
  canWrite: boolean
  limits: RawLimits
  freshKey: string
  onKey(key: string, limits: RawLimits, commands: InstallCommands): void
  onLimits(limits: RawLimits): void
}) {
  const [busy, setBusy] = useState(false)
  const [expires, setExpires] = useState(limits.expires_at ? String(limits.expires_at).substring(0, 10) : '')
  const [maxUses, setMaxUses] = useState(limits.max_uses ? String(limits.max_uses) : '')

  const ask = (what: 'create' | 'view') => {
    if (what === 'create' && hasKey) {
      const sure = window.confirm(
        'The current key stops working for new installs immediately.'
        + ' Machines already in the portal keep working. Continue?',
      )
      if (!sure) return
    }
    setBusy(true)
    const call = what === 'create' ? createEnrollKey() : viewEnrollKey()
    void call
      .then((answer) => {
        setBusy(false)
        onKey(answer.key, answer.limits, answer.commands)
        if (what === 'create') toast('Key generated. Copy the command below.', 'Agent Install')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'Agent Install', 'error')
      })
  }

  const save = () => {
    setBusy(true)
    void saveEnrollKeyLimits({ expires_at: expires, max_uses: maxUses })
      .then((answer) => {
        setBusy(false)
        onLimits(answer.limits)
        toast('Key limits saved.', 'Agent Install')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'Agent Install', 'error')
      })
  }

  return (
    <div className="od-da-card">
      <div className="od-da-key-row">
        <span className={hasKey ? 'od-da-pill od-da-pill-ok' : 'od-da-pill od-da-pill-wait'} id="odDaPill">
          <i className={hasKey ? 'fas fa-check-circle' : 'fas fa-key'}></i> {hasKey ? 'Key is set' : 'No key yet'}
        </span>
        <span className="od-da-muted" style={{ margin: 0 }} id="odDaKeyHint">
          {freshKey !== ''
            ? 'This is the key in use. Every view is logged.'
            : hasKey
              ? 'Already installed machines keep working. A new key is only for the next install.'
              : 'Until there is a key, a new Linux, Windows Server, Windows 10/11, or macOS install cannot join this company.'}
        </span>
        {canWrite ? (
          <>
            <span style={{ marginLeft: 'auto' }}></span>
            {/* Un buton care nu poate decat sa pice nu se deseneaza. Cheile
                facute inainte de pastrarea copiei cifrate nu se mai pot arata;
                pana acum omul afla abia dupa apasare, dintr-un mesaj rosu care
                suna a defect. Acum scrie de dinainte, unde statea butonul
                (cec-master 79062159f). */}
            {hasKey && !canShowKey ? (
              <span className="od-da-muted od-da-noshow" id="odDaNoShow">
                <i className="fas fa-info-circle"></i>
                This key was made before keys could be shown again, so it cannot be looked up. Nothing is broken and no
                machine is affected &mdash; <strong>Refresh key</strong> below gives one you can view.
              </span>
            ) : null}
            {hasKey && canShowKey ? (
              <button type="button" className="btn btn-success od-da-view" id="odDaShow" disabled={busy} onClick={() => ask('view')}>
                <i className="far fa-eye"></i> View key
              </button>
            ) : null}
            {!hasKey ? (
              <button type="button" className="btn btn-info btn-sm" id="odDaGen" disabled={busy} onClick={() => ask('create')}>
                <i className="fas fa-key"></i> Generate key
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {needsAdmin ? (
        <p className="od-da-warn" id="odDaNeedsAdmin" role="alert">
          <i className="fas fa-user-shield"></i> This company has no enrollment key yet, and you do not have the right to
          create one. Ask an administrator of your company to open <strong>Agent Install</strong> once &mdash; the key is
          created automatically.
        </p>
      ) : null}

      {freshKey !== '' ? (
        <div className="od-da-fresh is-on" id="odDaFresh">
          <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: '#9a7b12' }}>
            Copy this now. You can look it up again with View key; every look is written to the audit log.
          </p>
          <p className="od-da-key" id="odDaFreshKey">
            {freshKey}
          </p>
          <button type="button" className="btn btn-default btn-xs" onClick={() => copyText(freshKey, 'The key is in your clipboard.')}>
            <i className="far fa-copy"></i> Copy key
          </button>
          <p className="od-da-warn">
            Refreshing the key stops this one immediately. Machines already in the portal are not disconnected.
          </p>
        </div>
      ) : null}

      {hasKey ? (
        <div className="od-da-limits" id="odDaLimits">
          {limits.available ? (
            <>
              <span className="od-da-limits-uses">{usesText(limits)}</span>
              {canWrite ? (
                <>
                  <div>
                    <label htmlFor="odDaExpires">Expires on (optional)</label>
                    <input
                      type="date"
                      className="form-control"
                      id="odDaExpires"
                      value={expires}
                      onChange={(e) => setExpires(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="odDaMaxUses">Max uses (optional)</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      className="form-control"
                      id="odDaMaxUses"
                      placeholder="no limit"
                      value={maxUses}
                      onChange={(e) => setMaxUses(e.target.value)}
                    />
                  </div>
                  <button type="button" className="btn btn-default btn-sm" id="odDaSaveLimits" disabled={busy} onClick={save}>
                    <i className="far fa-save"></i> Save
                  </button>
                  <p className="od-da-limits-note">
                    Past the date or the count, the installer is refused with this key; machines already enrolled keep
                    working. Refreshing the key resets the count.
                  </p>
                </>
              ) : null}
            </>
          ) : (
            <p className="od-da-limits-note">Key expiry and use limits are not available on this installation yet.</p>
          )}
        </div>
      ) : null}

      {canWrite && hasKey ? (
        <div className="od-da-danger" id="odDaDanger">
          <div>
            <strong>Refresh key</strong>
            <p>
              Stops the current key immediately and gives a new one. Machines already in the portal keep working; only
              the next install needs the new key.
            </p>
          </div>
          <button type="button" className="btn btn-default btn-sm od-da-danger-btn" id="odDaMake" disabled={busy} onClick={() => ask('create')}>
            <i className="fas fa-sync"></i> Refresh key
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Instalarea in masa prin Group Policy.
 *
 * UN SINGUR FISIER DE DESCARCAT, si el poarta cheia firmei inauntru. Nu e un
 * `<a href>`: cheia face din descarcare acelasi fel de fapta ca "View key", deci
 * trece prin POST si se scrie in jurnal (vezi `downloadGpoPackage`).
 *
 * Se deseneaza numai cu dreptul de scriere si numai cand firma ARE cheie:
 * un buton care nu poate decat sa pice nu se deseneaza, aceeasi regula ca la
 * "View key" de mai sus.
 */
function GpoSection({ hasKey, canShowKey }: { hasKey: boolean; canShowKey: boolean }) {
  const [busy, setBusy] = useState(false)

  const get = () => {
    setBusy(true)
    void downloadGpoPackage()
      .then((name) => {
        setBusy(false)
        toast(`${name} is in your downloads. Copy it to a domain controller.`, 'Group Policy')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'Group Policy', 'error')
      })
  }

  /* Comanda aduce scriptul GENERIC de pe capatul public al portalului - fara
     cheie, fiindca se descarca fara sesiune. Cheia o cere scriptul la rulare,
     ca install.ps1 al agentilor: pe linia de comanda ar ramane in istoricul
     PowerShell al controlorului de domeniu. Adresa e a portalului pe care il
     ai deschis, nu dedusa din alta gazda. RemoteSigned, ca celelalte comenzi de
     pe pagina; Unblock-File e cel care il lasa sa ruleze fisierul descarcat. */
  const gpoScriptUrl = `${window.location.origin}/api/v2/public/gpo/starthere.ps1`
  const gpoCommand =
    'powershell -NoProfile -ExecutionPolicy RemoteSigned -Command "'
    + '[Net.ServicePointManager]::SecurityProtocol = [Net.SecurityProtocolType]::Tls12; '
    + `Invoke-WebRequest -UseBasicParsing -Uri '${gpoScriptUrl}' -OutFile (Join-Path $env:TEMP '${GPO_FILENAME}'); `
    + `Unblock-File (Join-Path $env:TEMP '${GPO_FILENAME}'); `
    + `& (Join-Path $env:TEMP '${GPO_FILENAME}')"`

  return (
    <aside className="od-da-side" id="odDaGpo">
      <div className="od-da-hero">
        <h2>Mass Deployment</h2>
        <p>
          With Group Policy: run it once on a domain controller and every Windows machine in the domain installs the
          agent by itself &mdash; nothing to type on any of them. It picks workstation or server per machine on its own.
        </p>
      </div>
      <div className="od-da-card">

      {!hasKey ? (
        <p className="od-da-warn" id="odDaGpoNoKey">
          Generate the company key first &mdash; the scripts are written around it.
        </p>
      ) : !canShowKey ? (
        <p className="od-da-warn" id="odDaGpoNoShow">
          This company&apos;s key predates keys that can be read back, so it cannot be written into the scripts. Use{' '}
          <strong>Refresh key</strong> above first.
        </p>
      ) : (
        <>
          <p className="od-da-muted" style={{ marginTop: 6 }}>
            Quickest: paste this into an elevated PowerShell on a domain controller. It downloads the script and runs
            it, and the script asks for the enrollment key &mdash; copy it with <strong>Copy key</strong> above.
          </p>
          <div className="od-da-term" id="odDaGpoCmd">
            {gpoCommand}
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm od-da-copy"
            id="odDaGpoCopy"
            onClick={() => copyText(gpoCommand, 'Group Policy command copied.')}
          >
            <i className="far fa-copy"></i> Copy command
          </button>
          <p className="od-da-muted" style={{ marginTop: 14 }}>
            Or download the file, with the key already inside:
          </p>
          <ol className="od-da-list">
            <li>
              Download <code>{GPO_FILENAME}</code> and copy it to a domain controller.
            </li>
            <li>
              Run it once there, in an elevated PowerShell:
              <div className="od-da-term" style={{ marginTop: 6 }}>
                powershell -NoProfile -ExecutionPolicy Bypass -File .\{GPO_FILENAME}
              </div>
            </li>
            <li>
              That is all it needs from you. It copies the two machine scripts into <code>NETLOGON</code>, creates the
              policy <strong>Deploy - ODAgent</strong> with an immediate task that runs as SYSTEM, and links it to the
              root of the domain. For part of the domain only, set <code>$TargetOUs</code> at the top of the file before
              you run it &mdash; an example is written next to it.
            </li>
            <li>
              Machines pick it up at the next policy refresh, up to 90 minutes, or straight away with{' '}
              <code>gpupdate /force</code>. Each one installs once and remembers it, so the policy can stay linked.
            </li>
          </ol>
          <p className="od-da-warn" id="odDaGpoRotate">
            The key is written into the copy the domain controller keeps in <code>NETLOGON</code>. If the deployment was
            started from the domain controller&apos;s <strong>Functions</strong> tab, refreshing the key updates that copy by
            itself. If you ran the command or the file by hand, run it again after a refresh &mdash; otherwise machines that
            have not installed yet will fail to enroll.
          </p>
          <p className="od-da-muted" style={{ margin: '12px 0 0' }}>
            The domain controller needs the <code>ActiveDirectory</code> and <code>GroupPolicy</code> PowerShell modules
            (RSAT). The script checks for them first and names the one that is missing, with the command that installs
            it.
          </p>
          <p className="od-da-warn">
            The key is written inside the file. <strong>After you refresh the key, download this again and run it again
            on the domain controller</strong> &mdash; the copy in NETLOGON does not update itself, and until you do, new
            installs are refused. Machines already enrolled keep working.
          </p>
          <button type="button" className="btn btn-primary" id="odDaGpoGet" disabled={busy} onClick={get} style={{ marginTop: 12 }}>
            <i className="fas fa-download"></i> Download Group Policy package
          </button>
        </>
      )}
      </div>
    </aside>
  )
}

function AfterInstall() {
  const cards: { kind: string; icon: string; title: string; meta: ReactNode; lines: ReactNode[] }[] = [
    {
      kind: 'od-da-linux',
      icon: 'fab fa-linux',
      title: 'Linux',
      meta: <>systemd service <code>od-linux-agent</code></>,
      lines: [
        <>Logs: <code>journalctl -u od-linux-agent -f</code></>,
        <>Errors: <code>/var/lib/od-agent/error.log</code></>,
        <>Config: <code>/etc/od-agent/config.json</code></>,
      ],
    },
    {
      kind: 'od-da-win',
      icon: 'fab fa-windows',
      title: 'Windows Server',
      meta: <>Service <code>OptimumDeskAgent</code>. Package <code>winserver-agent</code> only.</>,
      lines: [
        <>Services &rarr; OptimumDeskAgent</>,
        <>Errors: <code>%ProgramData%\od-agent\error.log</code></>,
        <>Config: <code>%ProgramData%\od-agent\config.json</code></>,
      ],
    },
    {
      kind: 'od-da-win',
      icon: 'fab fa-windows',
      title: 'Windows 10/11',
      meta: <>Service <code>OptimumDeskAgent</code>. Package <code>windows-agent</code> only.</>,
      lines: [
        <>Services &rarr; OptimumDeskAgent</>,
        <>Errors: <code>%ProgramData%\od-agent\error.log</code></>,
        <>Config: <code>%ProgramData%\od-agent\config.json</code></>,
      ],
    },
    {
      kind: 'od-da-mac',
      icon: 'fab fa-apple',
      title: 'macOS',
      meta: <>launchd <code>com.optimumdesk.agent</code>. Package <code>mac-agent</code> only.</>,
      lines: [
        <>Logs: <code>/var/lib/od-agent/agent.log</code></>,
        <>Errors: <code>/var/lib/od-agent/error.log</code></>,
        <>Config: <code>/etc/od-agent/config.json</code></>,
      ],
    },
  ]

  return (
    <div className="od-da-os">
      {cards.map((card) => (
        <div className={`od-da-card ${card.kind}`} key={card.title}>
          <div className="od-da-os-icon">
            <i className={card.icon}></i>
          </div>
          <h4>{card.title}</h4>
          <p className="od-da-meta">{card.meta}</p>
          <ul className="od-da-list">
            {card.lines.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

function Commands({ commands, versions }: { commands: InstallCommands; versions: AgentVersions }) {
  return (
    <div className="od-da-os">
      <CommandCard
        kind="od-da-linux"
        icon="fab fa-linux"
        title="Linux servers"
        version={versions.linux}
        meta="Ubuntu, Debian, RHEL, and other systemd hosts. Intel (amd64) and ARM (arm64). Run on the machine you want to enroll."
        command={commands.linux}
        copyLabel="Linux command copied."
      />
      <CommandCard
        kind="od-da-win"
        icon="fab fa-windows"
        title="Windows Server"
        version={versions.winserver}
        meta={
          <>
            Windows Server 2016 and newer. Not Windows 10/11 &mdash; those use the Windows 10/11 package next to this
            one. Paste into an elevated Command Prompt or PowerShell. The installer asks for the key: paste it at the
            prompt (<strong>Copy key</strong> above). It stays out of the PowerShell history that way.
          </>
        }
        command={commands.winserver}
        copyLabel="Windows Server command copied."
      />
      <CommandCard
        kind="od-da-win"
        icon="fab fa-windows"
        title="Windows 10/11"
        version={versions.windows}
        meta={
          <>
            Windows 10 and Windows 11. Not Windows Server &mdash; that uses the Windows Server package. Same service as
            Server: <code>OptimumDeskAgent</code>. The installer asks for the key: paste it at the prompt (
            <strong>Copy key</strong> above).
          </>
        }
        command={commands.windows}
        copyLabel="Windows 10/11 command copied."
      />
      <CommandCard
        kind="od-da-mac"
        icon="fab fa-apple"
        title="macOS"
        version={versions.mac}
        meta="Intel and Apple Silicon. This package refuses Linux and Windows. Run in Terminal with sudo."
        command={commands.mac}
        copyLabel="macOS command copied."
        note={
          <>
            macOS will ask for a password: type the password of your <strong>Mac administrator account</strong> (the one
            you use to unlock the Mac), not your OptimumDesk password.
          </>
        }
      />
    </div>
  )
}

export function DownloadAgentPage() {
  const query = useQueryClient()
  const shell = useQuery(downloadAgentShellQuery)
  const [freshKey, setFreshKey] = useState('')
  const [commands, setCommands] = useState<InstallCommands | null>(null)
  const [limits, setLimits] = useState<RawLimits | null>(null)
  const [hasKey, setHasKey] = useState<boolean | null>(null)
  const [canShowKey, setCanShowKey] = useState<boolean | null>(null)
  /* Prima cheie, facuta de server la deschidere, vine o singura data: se tine
     aici ca reincarcarea din fundal sa n-o stearga de pe ecran. */
  const autoKey = useRef<{ key: string; commands: InstallCommands } | null>(null)

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const page = shell.data
  if (autoKey.current === null && page.autoKey) {
    autoKey.current = { key: page.autoKey, commands: page.commands }
  }
  const liveLimits = limits ?? page.limits
  const liveCommands = commands ?? autoKey.current?.commands ?? page.commands
  const liveFreshKey = freshKey !== '' ? freshKey : (autoKey.current?.key ?? '')
  const liveHasKey = hasKey ?? page.hasKey
  const liveCanShowKey = canShowKey ?? page.canShowKey

  return (
    <div className="page-content-wrapper">
      {/* Doua coloane: Agent Install la latimea lui de totdeauna (980px), iar
          instalarea in masa alaturi, in dreapta - alta cale, nu alt pas. Cand
          nu incap amandoua, a doua trece dedesubt, ca Agent Install sa nu se
          ingusteze niciodata. */}
      <div className="od-da-cols">
      <div className="od-da">
        <div className="od-da-hero">
          <h2>Agent Install</h2>
          <p>
            First install on Linux, Windows Server, Windows 10/11, or macOS. Generate the company key, copy the command,
            run it as administrator. The machine appears in <strong>NEW</strong>.
            {page.companyName !== '' ? (
              <>
                {' '}
                This page is for <strong>{page.companyName}</strong>.
              </>
            ) : null}
          </p>
        </div>

        <Step
          number={1}
          title="Company enrollment key"
          lead={
            <>
              The installer presents this key once. After that the agent talks with a token bound to the machine, so
              refreshing the key does not knock the fleet offline.{' '}
              {liveCanShowKey || !liveHasKey ? (
                <>
                  You can look the key up again with <strong>View key</strong> &mdash; every look is written to the
                  audit log, together with every refresh.
                </>
              ) : (
                <>
                  Keys made from now on can be looked up again with <strong>View key</strong>; this company&apos;s key
                  predates that, so it can only be replaced. Every look and every refresh is written to the audit log.
                </>
              )}
            </>
          }
        >
          <KeyStep
            hasKey={liveHasKey}
            canShowKey={liveCanShowKey}
            canWrite={page.canWrite}
            limits={liveLimits}
            freshKey={liveFreshKey}
            needsAdmin={page.needsAdmin === true}
            onKey={(key, next, cmds) => {
              setFreshKey(key)
              setLimits(next)
              setCommands(cmds)
              setHasKey(true)
              /* O cheie facuta acum are copie cifrata: se poate arata. */
              setCanShowKey(true)
              /* Ecranul se reincarca in fundal: `hasKey` si limitele au putut
                 sa se schimbe si pentru cine deschide pagina din nou. */
              void query.invalidateQueries({ queryKey: ['settings', 'download-agent'] })
            }}
            onLimits={setLimits}
          />
        </Step>

        <Step
          number={2}
          title="Run the installer"
          lead={
            <>
              One command. It downloads the matching binary, writes the config, and starts the service. Root /
              Administrator is required. Running it again on a machine that already has the agent is a reinstall: it
              drops that machine&apos;s device secret and enrolls again.
              {page.apiUrl === '' ? (
                <>
                  <br />
                  <span style={{ color: '#c0392b' }}>
                    The API address is empty on this installation, so the commands below cannot be filled in. Check{' '}
                    <code>API_SERVER</code>.
                  </span>
                </>
              ) : null}
            </>
          }
        >
          <Commands commands={liveCommands} versions={page.versions} />
        </Step>

        <Step
          number={3}
          title="After the first install"
          lead={
            <>
              Wait about a minute. The device shows up under <strong>NEW</strong>. Assign it as usual. After that the
              agent keeps a token bound to the machine and does not call the enrollment key again. Refreshing the key
              only affects the <em>next</em> install; the fleet stays online. The same command, run again, is how you
              recover a machine whose secret no longer matches.
            </>
          }
        >
          <AfterInstall />
        </Step>

        {page.workstationUrl !== '' ? (
          <div className="od-da-card od-da-quiet" style={{ marginTop: 8 }}>
            <h4>
              <i className="fab fa-windows"></i> Windows workstations (classic 7.x)
            </h4>
            <p className="od-da-muted">
              The Go agent for Windows 10/11 is the command above. The classic 7.x installer remains here as a legacy
              download.
            </p>
            <a className="btn btn-default btn-sm" href={page.workstationUrl} target="_blank" rel="noopener noreferrer">
              <i className="fas fa-download"></i> Download workstation agent
            </a>
          </div>
        ) : null}
        {/* Numai pentru administratorii de platforma; pentru ceilalti nu se deseneaza. */}
        <AgentApiServerSection />
      </div>
      {page.canWrite ? <GpoSection hasKey={liveHasKey} canShowKey={liveCanShowKey} /> : null}
      </div>
    </div>
  )
}
